import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WidgetConfig, WidgetLayout } from '@rti/shared';
import WidgetWrapper from './WidgetWrapper';
import WidgetPicker from './WidgetPicker';
import { widgetRegistry } from './widgetRegistry';

const DEFAULT_LAYOUT: WidgetLayout = [
  { id: 'w1', componentKey: 'kpi-cards', position: 0 },
  { id: 'w2', componentKey: 'pe-fair-price', position: 1 },
  { id: 'w3', componentKey: 'pp-fair-price', position: 2 },
  { id: 'w4', componentKey: 'feedstock-daily', position: 3 },
  { id: 'w5', componentKey: 'petchem-averages', position: 4 },
  { id: 'w6', componentKey: 'pvc-fair-price', position: 5 },
];

function loadLayout(email: string): WidgetLayout | null {
  try {
    const raw = localStorage.getItem(`landing-layout-${email}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveLayout(email: string, layout: WidgetLayout) {
  try {
    localStorage.setItem(`landing-layout-${email}`, JSON.stringify(layout));
  } catch {}
}

interface WidgetGridProps {
  editMode: boolean;
}

const WidgetGrid: React.FC<WidgetGridProps> = ({ editMode }) => {
  const { user } = useAuth();
  const [layout, setLayout] = useState<WidgetLayout>(() => {
    if (user?.email) {
      return loadLayout(user.email) || DEFAULT_LAYOUT;
    }
    return DEFAULT_LAYOUT;
  });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [pickerTarget, setPickerTarget] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) {
      saveLayout(user.email, layout);
    }
  }, [layout, user?.email]);

  const handleRemove = useCallback((id: string) => {
    setLayout((prev) => prev.filter((w) => w.id !== id).map((w, i) => ({ ...w, position: i })));
  }, []);

  const handleDragStart = useCallback((id: string) => {
    setDraggedId(id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, _id: string) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }
    setLayout((prev) => {
      const next = [...prev];
      const draggedIdx = next.findIndex((w) => w.id === draggedId);
      const targetIdx = next.findIndex((w) => w.id === targetId);
      if (draggedIdx === -1 || targetIdx === -1) return prev;
      // Swap positions
      const temp = next[draggedIdx].position;
      next[draggedIdx] = { ...next[draggedIdx], position: next[targetIdx].position };
      next[targetIdx] = { ...next[targetIdx], position: temp };
      return next.sort((a, b) => a.position - b.position);
    });
    setDraggedId(null);
  }, [draggedId]);

  const handleReplace = useCallback((id: string) => {
    setPickerTarget(id);
  }, []);

  const handlePickerSelect = useCallback((targetId: string, componentKey: string) => {
    setLayout((prev) =>
      prev.map((w) => w.id === targetId ? { ...w, componentKey } : w)
    );
    setPickerTarget(null);
  }, []);

  const sorted = [...layout].sort((a, b) => a.position - b.position);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="widget-grid">
        {sorted.map((widget) => {
          const entry = widgetRegistry[widget.componentKey];
          if (!entry) return null;
          const Component = entry.component as React.FC<any>;
          return (
            <WidgetWrapper
              key={widget.id}
              id={widget.id}
              editMode={editMode}
              onRemove={handleRemove}
              onReplace={handleReplace}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="bg-rs-off-black/40 border border-rs-jet-light rounded-lg p-4 min-h-48">
                <p className="text-rs-off-white/50 text-sm mb-2">{entry.label}</p>
                <Suspense fallback={<div className="h-32 flex items-center justify-center text-rs-off-white/30 text-sm">Loading…</div>}>
                  <Component />
                </Suspense>
              </div>
            </WidgetWrapper>
          );
        })}
      </div>

      {pickerTarget && (
        <WidgetPicker
          targetId={pickerTarget}
          onSelect={handlePickerSelect}
          onClose={() => setPickerTarget(null)}
        />
      )}
    </>
  );
};

export default WidgetGrid;
